import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { SessionService } from '@core/services/session/session.service';
import { jwtDecode, JwtPayload } from 'jwt-decode';

/**
 * Verifica si un token JWT es válido.
 * @param {string} token - El token JWT a validar.
 * @returns {boolean} - Devuelve true si el token es válido, false en caso contrario.
 */
const isTokenValid = (token: string): boolean => {
  try {
    const currentToken = jwtDecode<JwtPayload>(token);
    if (currentToken.exp) {
      const today: Date = new Date();
      const tokenDate = new Date(0);
      tokenDate.setUTCSeconds(currentToken.exp);
      return tokenDate.getTime() > today.getTime();
    }
    return false;
  } catch (_error) {
    return false;
  }
};

/**
 * Intercepta las solicitudes HTTP para agregar una cabecera de autorización con un token JWT.
 * @param {HttpRequest<any>} req - La solicitud HTTP.
 * @param {HttpHandlerFn} next - La función de manejo HTTP.
 * @returns {Observable<any>} - Un observable que emite el resultado de la solicitud HTTP.
 */
export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const sessionService = inject(SessionService);

  const account = sessionService.getActiveAccount();
  if (!account) return next(req);

  const token = account.token;
  if (!token) return next(req);

  // Verifica si el token es válido
  if (!isTokenValid(token)) {
    sessionService.logout();
    return next(req);
  }

  // Detectar si la petición contiene FormData para subida de archivos
  const isFormData = req.body instanceof FormData;

  let headers = req.headers.set('Authorization', `Bearer ${token}`);

  // Solo establecer Content-Type y Accept si no es Cache-Control Y no es FormData
  if (!req.headers.get('Cache-Control') && !isFormData) {
    headers = headers.set('Content-Type', 'application/json').set('Accept', 'application/json');
  }
  return next(req.clone({ headers }));
};
